import { z } from 'zod';

/** Settings' tabs, in the order the strip shows them (spec 194 US1). */
export const SETTINGS_TABS = [
  'general',
  'people',
  'activity',
  'labels',
  // Moved here from its own sidebar entry (spec 224).
  'images',
] as const;

export type SettingsTab = (typeof SETTINGS_TABS)[number];

/** The parts of the People tab a link can land on (spec 193 US6). */
export const SETTINGS_SECTIONS = ['requests', 'members'] as const;

export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

/**
 * The address Settings had before its tabs were addresses (spec 194, 224): which
 * tab is open, which section of People to scroll to (spec 193), and whether to
 * open the Execution images add sheet. Only the forwarder reads it now (spec 227
 * FR-007): every value is carried to the tab's own address, and one the page does
 * not know is dropped rather than refused, so an old link still opens the page.
 */
export const legacySettingsSearchSchema = z.object({
  tab: z.enum(SETTINGS_TABS).optional().catch(undefined),
  section: z.enum(SETTINGS_SECTIONS).optional().catch(undefined),
  create: z.boolean().optional().catch(undefined),
});

export type LegacySettingsSearch = z.infer<typeof legacySettingsSearchSchema>;

/** People's own address: the section a link lands on (spec 193 US6). */
export const peopleSearchSchema = z.object({
  section: z.enum(SETTINGS_SECTIONS).optional().catch(undefined),
});

export type PeopleSearch = z.infer<typeof peopleSearchSchema>;

/**
 * Execution images' own address: the command palette's verb, and the old
 * `/execution-images?create` address, open the add sheet (spec 224).
 */
export const imagesSearchSchema = z.object({
  create: z.boolean().optional().catch(undefined),
});

export type ImagesSearch = z.infer<typeof imagesSearchSchema>;

/** Where an old Settings address lands (spec 227 FR-007). */
export interface SettingsTarget {
  tab: SettingsTab;
  /** Kept only for People: a section means nothing on another tab. */
  section?: SettingsSection;
  /** Kept only for Execution images. */
  create?: true;
}

/**
 * The tab an old address opens, and what it carries there. A section lives on
 * People, so a link that names only a section — every notification sent before
 * the tabs existed — opens People rather than General; `create` alone is the
 * palette's verb from before the tab had its own address, and opens Execution
 * images.
 */
export function settingsTargetOf(search: LegacySettingsSearch): SettingsTarget {
  const tab: SettingsTab =
    search.tab ??
    (search.section ? 'people' : search.create === true ? 'images' : 'general');
  if (tab === 'people' && search.section) {
    return { tab, section: search.section };
  }
  if (tab === 'images' && search.create === true) {
    return { tab, create: true };
  }
  return { tab };
}
